/**
 * Отображение даты пробного занятия (ТЗ «Дата пробного», §10).
 *
 * «Сегодня» считается по дате, которую вернул backend в таймзоне владельца:
 * локальные часы браузера администратора могут отличаться от часов филиала.
 */

const MONTHS_GENITIVE = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
];

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function isIsoDate(value) {
  return typeof value === 'string' && ISO_DATE_RE.test(value);
}

/**
 * Сегодняшняя дата по часам браузера. Используется как стартовое значение,
 * пока backend не прислал дату в таймзоне владельца.
 */
export function localIsoToday() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

/** «2026-08-15» → «15 августа». */
export function formatTrialDateRu(date) {
  if (!isIsoDate(date)) return '—';
  const [, month, day] = date.split('-');
  return `${Number(day)} ${MONTHS_GENITIVE[Number(month) - 1] ?? ''}`.trim();
}

/** «2026-08-15» → «15.08.2026» — для таблиц истории. */
export function formatTrialDateShort(date) {
  if (!isIsoDate(date)) return '—';
  const [year, month, day] = date.split('-');
  return `${day}.${month}.${year}`;
}

/** Сдвиг ISO-даты на N дней без участия таймзоны браузера. */
export function shiftIsoDate(date, days) {
  const base = new Date(`${date}T00:00:00Z`);
  base.setUTCDate(base.getUTCDate() + days);
  return base.toISOString().slice(0, 10);
}

/** Понедельник–воскресенье недели, в которую попадает дата. */
export function isoWeekRange(date) {
  const base = new Date(`${date}T00:00:00Z`);
  const weekday = base.getUTCDay() === 0 ? 7 : base.getUTCDay();
  return { from: shiftIsoDate(date, 1 - weekday), to: shiftIsoDate(date, 7 - weekday) };
}

/**
 * Подпись даты пробного для карточки.
 * `overdue` — дата прошла, а клиент всё ещё ждёт пробного (ТЗ §10).
 */
export function describeTrialDate(date, today) {
  if (!isIsoDate(date)) return null;
  if (isIsoDate(today)) {
    if (date === today) return { text: 'Пробное: сегодня', tone: 'today' };
    if (date === shiftIsoDate(today, 1)) return { text: 'Пробное: завтра', tone: 'soon' };
    if (date < today) {
      return { text: `Пробное было назначено на ${formatTrialDateRu(date)}`, tone: 'overdue' };
    }
  }
  return { text: `Пробное: ${formatTrialDateRu(date)}`, tone: 'planned' };
}

/** Ожидает ли статус пробного — только тогда прошедшая дата является проблемой. */
export function isAwaitingTrial(systemType) {
  return systemType === 'trial' || systemType === 'lead';
}

export const TRIAL_DATE_CHANGE_TYPE_LABELS = {
  scheduled: 'Назначено',
  rescheduled: 'Перенесено',
  manual_correction: 'Ручная правка',
  system: 'Системой',
};

/** Машинные коды 422 с бэкенда → текст, понятный администратору. */
export const TRIAL_DATE_ERROR_LABELS = {
  TRIAL_DATE_REQUIRED: 'Укажите дату пробного занятия',
  TRIAL_DATE_MUST_CHANGE: 'Новая дата должна отличаться от текущей',
  TRIAL_DATE_IN_PAST: 'Дата пробного не может быть в прошлом',
};
